import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import FormatResourceStatus from "../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { PartnerApi } from "../../../../../../../../services/Api/Partner/partnerTypes";
import { api } from "../../../../../../../../services/Api/api";
import PartnerForm from "../../../Partners/components/PartnerForm";

interface MatchParams {
	partnerId: string;
}

const AdminPartnersEditPage = () => {
	const [partner, setPartner] = useState<PartnerApi | undefined>();
	const [customFieldsConfig, setCustomFieldsConfig] = useState<CustomFieldTemplateApi[]>([]);
	const history = useHistory();
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addFlash, addSuccessFlash } = useFlash();
	const isMobile = useWindowSize().isMobile;
	const { partnerId } = useParams<MatchParams>();
	const params = {
		include: "users,organizations,codes,partners,provisions,parent,custom_fields",
	};
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchPartner();
	}, []);

	const fetchPartner = async () => {
		try {
			await fetchResourceConfig();
			const res = await adminApi.getAdminPartner(Number(partnerId), params);
			setPartner(res);
			handleChangeTabTitle(
				`${res.name} | ${t("modules.partner.header.title")} | ${t("lib:go_component.navigation.admin")}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchResourceConfig = async () => {
		try {
			const params = {
				resource_name: "ADMIN_PARTNER",
				custom_field_resource_type: "PARTNER",
				custom_field_permission_scope: "WRITE",
			};
			const res = await api.getResourceConfig(params);
			setCustomFieldsConfig(res.custom_fields);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const removePartner = async (item: PartnerApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.remove", { ns: "lib" }),
			});
			await adminApi.removeAdminPartner(item.id);
			addSuccessFlash(t("lib:common.flash.removed"));
			history.push(`/admin/partners`);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const restorePartner = async (item: PartnerApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.activate", { ns: "lib" }),
			});
			const resp = await adminApi.restoreAdminPartner(item.id, params);
			setPartner(resp);
			addSuccessFlash(t("lib:common.flash.restored"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};
	const onEnablePartner = async (item: PartnerApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("lib:confirmation.message.action"),
			});
			const resp = await adminApi.enableAdminPartner(item.id, params);
			setPartner(resp);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};
	const onDisablePartner = async (item: PartnerApi) => {
		try {
			await confirmation({
				title: t("confirmation.title", { ns: "lib" }),
				message: t("confirmation.message.disable", { ns: "lib" }),
			});
			const resp = await adminApi.disableAdminPartner(item.id, params);
			setPartner(resp);
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!partner) {
		return <LoadingContainer />;
	}

	const renderTitle = () => (
		<>
			{t("modules.partner.header.edit.title")} {partner?.name} <FormatResourceStatus status={partner.status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			hide: partner.status === "DELETED",
			title: t("common.action.remove", { ns: "lib" }),
			action: () => removePartner(partner),
			dropdown: true,
		},
		{
			hide: partner.status !== "DELETED",
			title: t("common.action.restore", { ns: "lib" }),
			action: () => restorePartner(partner),
			dropdown: true,
		},
		{
			hide: partner.status !== "ENABLED",
			title: t("common.action.deactivate"),
			action: () => onDisablePartner(partner),
			dropdown: true,
		},
		{
			hide: partner.status !== "DISABLED",
			title: t("lib:common.action.activate"),
			action: () => onEnablePartner(partner),
			dropdown: true,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			hide: partner.status === "DELETED",
			title: t("common.action.remove", { ns: "lib" }),
			action: () => removePartner(partner),
		},
		{
			hide: partner.status !== "DELETED",
			title: t("common.action.restore", { ns: "lib" }),
			action: () => restorePartner(partner),
		},
		{
			hide: partner.status !== "ENABLED",
			title: t("common.action.deactivate"),
			action: () => onDisablePartner(partner),
		},
		{
			hide: partner.status !== "DISABLED",
			title: t("lib:common.action.activate"),
			action: () => onEnablePartner(partner),
		},
	];

	return (
		<div className="content">
			<MobileActions actions={mobileActions} />
			<Header title={renderTitle()} buttons={isMobile ? [] : buttons} />
			<PartnerForm partner={partner} customFieldsConfig={customFieldsConfig} />
		</div>
	);
};

export default AdminPartnersEditPage;
